import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { DashboardService } from '../../Modules/Dashboard/Services/dashboard.service';
import { PlanService } from '../../Modules/Plans/Services/plan.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { SystemUserService } from '../../Modules/Users/Services/system-user.service';
import { UserService } from '../../Modules/Users/Services/user.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { OperatorFacade } from '../operator.facade';
import { OrganizationStatus } from '../../Enums/organization-status.enum';
import { SystemRole } from '../../Enums/system-role.enum';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { PlanRevenue } from '../../Models/backoffice/plan-revenue.model';
import { PlanSummary } from '../../Models/backoffice/plan-summary.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationListResponse } from '../../Interfaces/api/backoffice/organization.interface';
import {
  SystemUserListResponse,
  TenantUserListResponse,
} from '../../Interfaces/api/backoffice/user.interface';
import { PlanListResponse } from '../../Interfaces/api/backoffice/plan.interface';
import { MarketListResponse } from '../../Interfaces/api/backoffice/market.interface';

@Injectable()
export class DashboardFacade {
  private readonly _dashboardService = inject(DashboardService);
  private readonly _planService = inject(PlanService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _userService = inject(UserService);
  private readonly _systemUserService = inject(SystemUserService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  readonly canReadTenantUsers = computed<boolean>(() =>
    this._operatorFacade.atLeast(SystemRole.BACKOFFICE_AGENT),
  );

  readonly canReadOperators = computed<boolean>(() =>
    this._operatorFacade.atLeast(SystemRole.BACKOFFICE_ADMIN),
  );

  private readonly _revenues = signal<PlanRevenue[]>([]);
  private readonly _revenueState = signal<MetricState>('loading');

  private readonly _organizationsTotal = httpResource<
    LaravelDefaultResponse<OrganizationListResponse>
  >(() => this._organizationService.countRequest());

  private readonly _organizationsActive = httpResource<
    LaravelDefaultResponse<OrganizationListResponse>
  >(() => this._organizationService.countRequest(OrganizationStatus.ACTIVE));

  private readonly _organizationsSuspended = httpResource<
    LaravelDefaultResponse<OrganizationListResponse>
  >(() => this._organizationService.countRequest(OrganizationStatus.SUSPENDED));

  private readonly _organizationsCancelled = httpResource<
    LaravelDefaultResponse<OrganizationListResponse>
  >(() => this._organizationService.countRequest(OrganizationStatus.CANCELLED));

  private readonly _tenantUsers = httpResource<LaravelDefaultResponse<TenantUserListResponse>>(
    () => (this.canReadTenantUsers() ? this._userService.countRequest() : undefined),
  );

  private readonly _operators = httpResource<LaravelDefaultResponse<SystemUserListResponse>>(() =>
    this.canReadOperators() ? this._systemUserService.countRequest() : undefined,
  );

  private readonly _plans = httpResource<LaravelDefaultResponse<PlanListResponse>>(() =>
    this._planService.listRequest(),
  );

  private readonly _markets = httpResource<LaravelDefaultResponse<MarketListResponse>>(() =>
    this._planService.marketsRequest(),
  );

  readonly organizationsTotal = computed<number>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizationsTotal)).total,
  );

  readonly organizationsActive = computed<number>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizationsActive)).total,
  );

  readonly organizationsSuspended = computed<number>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizationsSuspended)).total,
  );

  readonly organizationsCancelled = computed<number>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizationsCancelled)).total,
  );

  readonly tenantUsersTotal = computed<number>(
    () => this._userService.toPagedResult(this._resourceState.valueOf(this._tenantUsers)).total,
  );

  readonly operatorsTotal = computed<number>(() =>
    this._systemUserService.toTotal(this._resourceState.valueOf(this._operators)),
  );

  readonly plans = computed<PlanSummary[]>(() => this._planService.toPlans(this._resourceState.valueOf(this._plans)));

  readonly activePlans = computed<PlanSummary[]>(() =>
    this.plans().filter((plan) => plan.is_active),
  );

  readonly subscriptionsTotal = computed<number>(() =>
    this.plans().reduce((sum, plan) => sum + plan.subscriber_count, 0),
  );

  readonly currency = computed<string | null>(() =>
    this._planService.defaultCurrency(this._planService.toMarkets(this._resourceState.valueOf(this._markets))),
  );

  readonly revenues = computed<PlanRevenue[]>(() => this._revenues());

  readonly monthlyRevenue = computed<number>(() =>
    this._revenues().reduce((sum, row) => sum + row.monthlyRevenue, 0),
  );

  readonly revenueIsPartial = computed<boolean>(() =>
    this._revenues().some((row) => row.isPartial),
  );

  readonly organizationsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._organizationsTotal.status()),
  );

  readonly statusSplitState = computed<MetricState>(() =>
    this._resourceState.metricState(this._organizationsActive.status()),
  );

  readonly tenantUsersState = computed<MetricState>(() =>
    this._resourceState.metricState(this._tenantUsers.status(), this.canReadTenantUsers()),
  );

  readonly operatorsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._operators.status(), this.canReadOperators()),
  );

  readonly plansState = computed<MetricState>(() =>
    this._resourceState.metricState(this._plans.status()),
  );

  readonly marketsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._markets.status()),
  );

  readonly revenueState = computed<MetricState>(() => this._revenueState());

  constructor() {
    this._registerRevenueScan();
  }

  reload(): void {
    this._organizationsTotal.reload();
    this._organizationsActive.reload();
    this._organizationsSuspended.reload();
    this._organizationsCancelled.reload();
    this._tenantUsers.reload();
    this._operators.reload();
    this._plans.reload();
    this._markets.reload();
  }

  private _registerRevenueScan(): void {
    effect(() => {
      const plans = this.activePlans();
      const state = this.plansState();

      this._scanRevenue(plans, state);
    });
  }

  private _scanRevenue(plans: PlanSummary[], state: MetricState): void {
    if (state === 'error') {
      this._revenueState.set('error');

      return;
    }

    if (state !== 'ready') {
      this._revenueState.set('loading');

      return;
    }

    if (plans.length === 0) {
      this._revenues.set([]);
      this._revenueState.set('ready');

      return;
    }

    this._loadRevenue(plans);
  }

  private _loadRevenue(plans: PlanSummary[]): void {
    this._revenueState.set('loading');

    forkJoin(plans.map((plan) => this._dashboardService.planRevenue(plan)))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (rows) => this._applyRevenue(rows),
        error: () => this._revenueState.set('error'),
      });
  }

  private _applyRevenue(rows: PlanRevenue[]): void {
    this._revenues.set(rows);
    this._revenueState.set('ready');
  }
}
